import { useEffect, useId, useRef, type ReactNode } from "react";

/**
 * Built on <dialog> so focus trapping, Escape, and inertness of the page behind
 * come from the platform rather than from hand-rolled key handlers.
 *
 * The children are mounted only while it is open, and that is a correctness
 * property rather than a saving. A closed <dialog> is hidden by `display: none`,
 * not unmounted — so children rendered unconditionally are mounted from the
 * first paint of the page behind, with whatever props they had *then*. Any form
 * that reads its initial state from a prop therefore reads it before an operator
 * has chosen anything, and `useState` initialisers do not run again: the
 * promotions editor opened blank on every campaign, and every field had to be
 * retyped from the row the operator was looking at.
 *
 * Two call sites had already worked around it in two different ways — one by
 * guarding its own children, one by setting every field by hand before opening —
 * which is the shape of an API defaulted the wrong way. Opening now mounts
 * fresh, so a form initialised from props is correct without either workaround.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Keep a mutation modal in place until its request settles. */
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(event) => {
        if (!dismissable) event.preventDefault();
      }}
      onClick={(event) => {
        // Clicking the backdrop lands on the dialog element itself.
        if (dismissable && event.target === ref.current) onClose();
      }}
      aria-labelledby={titleId}
      /*
       * `open:flex` rather than a bare `flex`: a closed <dialog> is hidden by
       * `display: none`, and an unconditional `flex` overrides that and renders
       * the thing inline on the page. The variant only applies while the
       * `open` attribute is present, so the platform keeps its own hiding.
       *
       * `85dvh` and not `85vh` — on iOS Safari `vh` is the viewport *with the
       * address bar retracted*, so a modal sized in `vh` is taller than what
       * you can actually see until you scroll the page behind it.
       */
      className="m-auto max-h-[85dvh] w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface p-0 text-fg backdrop:bg-canvas/70 open:flex open:flex-col"
    >
      <div className="shrink-0 border-b border-line px-5 py-4">
        <h2 id={titleId} className="font-semibold">
          {title}
        </h2>
        {description && (
          <p className="mt-0.5 text-sm text-fg-subtle">{description}</p>
        )}
      </div>
      {/* The body is the only part that scrolls, so the title stays readable on
          a phone no matter how long the content is — the plan editor and the
          server-registration form both run well past a 667px screen. */}
      {/* Mounted per opening: see above. A form that reads its initial state
          from a prop gets the value the operator just chose, rather than the
          one that was there when the page first rendered. */}
      {/*
       * `flex-auto` and not `flex-1`, and the difference is Safari.
       *
       * `flex-1` is `flex: 1 1 0%`. The UA stylesheet sizes a `<dialog>` with
       * `height: fit-content`, so this column has no definite height and the
       * browser sizes it from its children's max-content contributions. Chrome
       * and Firefox use the body's real content height; **Safari uses the flex
       * basis**, which is `0` — the body contributes nothing, the dialog
       * collapses to header plus footer, and the content is clipped to a sliver
       * with the footer riding up over it.
       *
       * `flex: 1 1 auto` makes the basis the content, so every engine agrees.
       * It still shrinks and scrolls once `max-h-[85dvh]` binds, because
       * `flex-shrink` is 1 and `min-h-0` lets it go under its content.
       *
       * The same line in `maxlayer-clients` had the same bug; it was reported
       * there and found here by looking for the pattern rather than waiting for
       * a second report.
       */}
      <div className="min-h-0 flex-auto overflow-y-auto px-5 py-4">{open && children}</div>
    </dialog>
  );
}
